import React from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import {imageProvider} from 'utils/imageProvider'

import {Icon} from '@vio/atlas/Icon'
import {Image} from '@vio/atlas/Image'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {getIsBrandVio} from '@vio/core/src/_web/brand/modules/selectors'
import {Room} from '@vio/core/src/room/types/room'

import {
  GalleryGrid,
  GalleryWrapper,
  GridItem,
  ImageCountBadge,
  NoImagePlaceholder,
  SingleImageWrapper
} from './styles'

const NUM_IMAGES_TO_DISPLAY = 3

interface Props {
  /** Room data containing images */
  images: Room['images']
  /** Callback when clicking on the gallery */
  onClick?: () => void
  /** Indicating if this is a Dateless RoomGallery */
  isDateless?: boolean
}

/**
 * RoomGalleryTiles displays a grid of room images with the following behavior:
 * - 3+ images: Shows 3 images in a grid layout with image count badge
 * - 1-2 images: Shows only the first image
 * - 0 images: Shows nothing on Mobile and placeholder on desktop
 *
 * Mobile layout: 9:4 aspect ratio with horizontal grid (large left, two stacked right)
 * Desktop layout: Vertical layout with fixed 290px width
 */
export const RoomGalleryTiles: React.FC<Props> = ({
  images: roomImages,
  onClick,
  isDateless = false
}) => {
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const images = roomImages?.map(img => img.url) || []
  const totalImageCount = images.length
  const isBrandVio = useSelector(getIsBrandVio)

  // No images case
  if (totalImageCount === 0) {
    if (isMobile || isDesktopXs) return null
    return (
      <NoImagePlaceholder
        isBrandVio={isBrandVio}
        data-testid="room-card-gallery-placeholder"
      />
    )
  }

  const imagesToRender = images.slice(0, NUM_IMAGES_TO_DISPLAY)

  return (
    <GalleryWrapper
      className={cx('--room-card-gallery', isDateless && '--is-dateless')}
      data-testid="room-card-gallery"
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      {totalImageCount < NUM_IMAGES_TO_DISPLAY || isDateless ? (
        <SingleImageWrapper>
          <Image
            url={images[0]}
            imageProvider={imageProvider}
            size="main"
            isResponsive
          />
        </SingleImageWrapper>
      ) : (
        <GalleryGrid>
          {imagesToRender.map((image, index) => (
            <GridItem key={index}>
              <Image
                url={image}
                imageProvider={imageProvider}
                size={index === 0 ? 'main' : 'gridSecondary'}
                isResponsive
              />
            </GridItem>
          ))}
        </GalleryGrid>
      )}
      <ImageCountBadge>
        <Icon name="Photo" size="sm" />
        <Text variant="labelXS" colorPath="content.neutral.inverse">
          {totalImageCount}
        </Text>
      </ImageCountBadge>
    </GalleryWrapper>
  )
}
